import { dec, formatMoney, ZERO } from "@bl/domain";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  BookOpen,
  Check,
  FlaskConical,
  RotateCcw,
  Save,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { type BrewDetail, keys, send, useBrew, useChange, useRecipe } from "../api";
import { type SaveStatus, saveStatusText, sendAutosave, useAutosave } from "../autosave";
import {
  amountOf,
  type BrewLine,
  type DraftPull,
  decimalOnly,
  inUnit,
  lotStanding,
  NONE,
  notInInventoryOf,
  overBy,
  replanned,
  shortOf,
} from "../brewPulls";
import { ago, daysSince, STAGE_LABELS } from "../components/BrewPills";
import { PrintSheets } from "../components/PrintSheets";
import {
  Dialog,
  DialogForm,
  ErrorNote,
  Toolbar,
  ToolbarSeparator,
  usePageTitle,
} from "../components/ui";
import { showDate, showDateTime } from "../format";
import { HarvestDialog, harvestable } from "../forms/HarvestDialog";
import { MovementDialog } from "../forms/MovementDialog";
import { useKeptOnLeaving, useNotify } from "../notify";
import { showFahrenheit, showGallons, showGravity } from "../recipeFormat";
import { BrewTanks } from "./BrewTanks";
import {
  type Draft,
  gallons,
  localKeyOf,
  patchOf,
  pulledLine,
  STAGES,
  type Stage,
  stageOf,
  startingDraft,
  stepKey,
  typedStage,
  whirlpoolStepsOf,
} from "./brewDraft";
import { BrewDayField, PlannedBrew, PrintButton, StatusChip } from "./PlannedBrew";
import { lineName, PullCard } from "./PullCard";
import { beerName } from "./Repitch";

export function BrewPage() {
  const { brewId } = useParams({ from: "/brews/$brewId" });
  const { data: brew, isLoading, error } = useBrew(brewId);
  usePageTitle(
    brew ? beerName({ recipeName: brew.recipe.name, number: brew.number }) : null,
    "Brew day",
  );
  if (isLoading) return <div className="page-body muted">Loading</div>;
  if (error || !brew) {
    return (
      <div className="page-body">
        <ErrorNote error={error ?? new Error("Brew not found")} />
      </div>
    );
  }
  if (brew.status === "planned") return <PlannedBrew brew={brew} />;
  // Brewing or undoing starts the form afresh; its own autosaves do not, or typing would be lost.
  return <BrewForm key={`${brew.id} ${brew.status}`} brew={brew} />;
}
function BrewForm({ brew: b }: { brew: BrewDetail }) {
  const dayId = useId();
  // The version it brews, for its printed Brew Log and recipe sheet.
  const { data: recipe } = useRecipe(b.recipe.id);
  // A stage tapped that the readings keep it past: the one it stays at, said under the strip.
  const [keptAt, setKeptAt] = useState<string | null>(null);
  const [start] = useState(() => startingDraft(b));
  const [draft, setDraft] = useState<Draft>(start.draft);
  const [confirming, setConfirming] = useState<"brew" | "undo" | "delete" | null>(null);
  const [showPulls, setShowPulls] = useState(false);
  const [harvesting, setHarvesting] = useState(false);
  // Stock received or counted from this page, so nothing typed on it is lost.
  const [stocking, setStocking] = useState<{ itemId: string; mode: "receive" | "adjust" } | null>(
    null,
  );
  const notify = useNotify();
  const navigate = useNavigate();
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setPulls = (lineId: string, pulls: DraftPull[]) =>
    setDraft((d) => ({ ...d, pulls: { ...d.pulls, [lineId]: pulls } }));

  // Brew day saves itself as it is typed into, and as it is left (Tyler, 2026-10-01): an unfinished
  // brew stays in progress, to be picked up from its pill in the header on this phone or any other.
  const queryClient = useQueryClient();
  const latest = useRef(b);
  latest.current = b;
  const autosave = useAutosave<Draft>({
    localKey: localKeyOf(b.id),
    save: async ({ value, savedAt }, keepalive) => {
      const result = await sendAutosave(
        "PUT",
        `/brews/${b.id}/autosave`,
        { savedAt, patch: patchOf(latest.current, value) },
        keepalive,
      );
      if (result === "saved") {
        // The estimates and shortfalls follow what was saved; the list shows the brew as it is now.
        void queryClient.invalidateQueries({ queryKey: keys.brew(b.id) });
        void queryClient.invalidateQueries({ queryKey: keys.brews, exact: true });
      }
      return result;
    },
    // Brewed or emptied elsewhere: show the brew as the server has it.
    onRejected: () => void queryClient.invalidateQueries({ queryKey: keys.brews }),
  });
  // Sent whenever it differs from what was last sent; the page as it opened only when it never got
  // to the server.
  const sent = useRef<Draft | null>(start.unsent ? null : start.draft);
  useEffect(() => {
    if (draft === sent.current) return;
    sent.current = draft;
    autosave.update(draft);
  }, [draft, autosave.update]);

  // Leaving a brew in progress that was typed into says where it waits, and tapping that opens it
  // again (Tyler, 2026-10-01). Brewing it or deleting it leaves nothing waiting; the form also goes
  // when the brew is brewed, as it is shown anew.
  const departed = useKeptOnLeaving(() =>
    autosave.status !== "idle" && b.status === "draft"
      ? {
          title: "Brew kept",
          body: `${b.recipe.name} is still in progress. It opens again from the brew pills in the header, beside the search.`,
          link: { kind: "brew", brewId: b.id },
        }
      : null,
  );

  const brewIt = useChange(
    async (_: undefined, key) => {
      // What is on the screen is what is taken: anything still saving lands first, then this.
      await autosave.settle();
      try {
        await send.saveBrew({ id: b.id, patch: patchOf(b, draft) }, `${key}-save`);
        const done = await send.brewed(b.id, key);
        departed.current = true;
        return done;
      } catch (error) {
        // Not brewed after all: what is on the screen goes on being kept.
        autosave.update(draft);
        throw error;
      }
    },
    {
      onSuccess: () => {
        notify({ title: "Brewed, and taken from inventory", link: { kind: "brew", brewId: b.id } });
        window.scrollTo({ top: 0 });
      },
    },
  );
  const undo = useChange((_: undefined, key) => send.undoBrew(b.id, key), {
    onSuccess: () => notify("Undone: everything it took is back in inventory"),
  });
  const remove = useChange(
    async (_: undefined, key) => {
      await autosave.settle();
      const gone = await send.deleteBrew(b.id, key);
      departed.current = true;
      return gone;
    },
    { onSuccess: () => navigate({ to: "/production" }) },
  );

  const brewed = b.status === "brewed";
  const linked = b.lines.filter((l) => l.item);
  const unlinked = b.lines.filter((l) => !l.item);
  // Yeast is pitched at the fermenter, not pulled with the grain and hops, and after a strain's first
  // brew it often comes from another beer's fermenter: it has its own place on the page (Scott's Jam,
  // 2026-10-08: "yeast will need to be in a separate area").
  const pullLines = linked.filter((l) => l.kind !== "yeast");
  const yeastLines = linked.filter((l) => l.kind === "yeast");
  // What leaves inventory: the pulls from a lot, not what is not in inventory.
  const pulls = linked.flatMap((l) =>
    (draft.pulls[l.recipeLineId] ?? []).filter(
      (p) => p.lotKey && p.lotKey !== NONE && amountOf(p.amount) > 0,
    ),
  );
  const shortLines = linked.filter((l) => shortOf(l, draft.pulls[l.recipeLineId] ?? []) > 0);
  // Brewed is not held for stock inventory does not have (Scott, 2026-10-02): what no lot covers, and
  // what a lot is asked for beyond what it holds, is kept on the brew to give a lot later, and the
  // brew is flagged until it is. The page says so before Brewed; the server does the sums again.
  const standing = lotStanding(linked, draft.pulls);
  const notInInventory = brewed
    ? []
    : linked.filter(
        (l) =>
          notInInventoryOf(draft.pulls[l.recipeLineId] ?? []) > 0 ||
          (draft.pulls[l.recipeLineId] ?? []).some((p) => overBy(standing.get(p.lotKey)) > 0),
      );
  const toAssign = brewed ? linked.filter((l) => l.pulls.some((p) => p.lotId === null)) : [];
  const replan = (itemId: string) =>
    setDraft((d) => ({ ...d, pulls: { ...d.pulls, ...replanned(linked, itemId, d.pulls) } }));
  const e = b.estimates;
  const steps = whirlpoolStepsOf(b);
  const firstStepF = draft.whirlpoolF[stepKey(steps[0] ?? [])] ?? "";
  const ibuBecause = ibuReasons(b, steps);
  const stage = stageOf(draft);
  const at = STAGES.findIndex((x) => x.id === stage);
  // Once the mash has started the pulls are done with: they fold to a ruled list, and a line that
  // still needs a look (short, or more than its lot holds) stays open above it.
  const attention = new Set([...shortLines, ...notInInventory].map((l) => l.recipeLineId));
  const ready = pullLines.filter((l) => !attention.has(l.recipeLineId));
  const folded = !brewed && stage !== "pull" && !showPulls;
  // Where the beer goes on Brewed: the tanks booked from its brew day.
  const into = b.stays.filter((s) => s.state === "booked" && s.inOn <= draft.brewedOn);
  const goTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ block: "start", behavior: "auto" });

  return (
    <>
      <Toolbar
        left={
          <>
            <Link to="/production" className="toolbar-button" title="Back to Production">
              <ArrowLeft size={20} />
              <span className="label">Production</span>
            </Link>
            <ToolbarSeparator />
          </>
        }
        right={
          <>
            <Link
              to="/recipes/$recipeId"
              params={{ recipeId: b.recipe.id }}
              className="toolbar-button"
              title="The recipe"
            >
              <BookOpen size={20} />
              <span className="label">Recipe</span>
            </Link>
            <PrintButton />
          </>
        }
      />
      <div className="page-body centered brew-page">
        <section className="brew-card brew-hero">
          <div className="brew-hero-head">
            <div>
              <div className="muted">
                Brew {b.number}
                {b.recipe.version > 1 ? `, version ${b.recipe.version}` : ""}
                {`, ${showGallons(b.batchLiters)}`}
              </div>
              <h1>{b.recipe.name}</h1>
            </div>
            <StatusChip brew={b} />
          </div>
          <div className="brew-field">
            <label htmlFor={dayId}>Brew day</label>
            <BrewDayField
              id={dayId}
              value={draft.brewedOn}
              disabled={brewed}
              onDay={(brewedOn) => set({ brewedOn })}
            />
          </div>
          {/* A day gone and not Brewed: what to do about it, here where it can be done (the
              re-critique, 2026-10-07: the brew's own page gave no age at all). */}
          {!brewed && draft.brewedOn && daysSince(draft.brewedOn) >= 1 ? (
            <p className="brew-flag brew-stale">
              <TriangleAlert size={14} aria-hidden /> Brew day was {showDate(draft.brewedOn)},{" "}
              {ago(daysSince(draft.brewedOn))}, and it is not marked Brewed, so what it pulls still
              counts as on hand. Press Brewed once it is in a tank, or delete it at the foot of the
              page.
            </p>
          ) : null}
          {brewed ? null : (
            // One tap says where the brew is, for the header and every other device, and goes there.
            <ol className="brew-steps" aria-label="Where this brew is: tap the stage you are at">
              {STAGES.map((st, i) => {
                const state = i < at ? "done" : i === at ? "now" : "next";
                return (
                  <li key={st.id} className={state}>
                    <button
                      type="button"
                      aria-current={state === "now" ? "step" : undefined}
                      title={`At the ${st.label.toLowerCase()} now`}
                      onClick={() => {
                        set({ stage: st.id });
                        // A stage before the readings typed cannot be said: say why, rather than
                        // nothing (the re-critique, 2026-10-07).
                        const typed = typedStage(draft);
                        setKeptAt(
                          STAGES.findIndex((x) => x.id === typed) > i ? STAGE_LABELS[typed] : null,
                        );
                        // After the page has laid itself out for that stage.
                        requestAnimationFrame(() => goTo(`brew-${st.id}`));
                      }}
                    >
                      {state === "done" ? (
                        <>
                          <Check size={16} aria-hidden />
                          <span className="sr-only">Done: </span>
                        </>
                      ) : null}
                      {st.label}
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          {!brewed && keptAt && keptAt === STAGE_LABELS[typedStage(draft)] ? (
            <p className="muted brew-kept" role="status">
              The {keptAt.toLowerCase()} readings are in, so it stays at {keptAt}. It goes back once
              they are cleared.
            </p>
          ) : null}
        </section>

        {/* The pulls, then the day's readings and notes: one column on a phone, side by side given room. */}
        <div className={`brew-columns ${!brewed && stage !== "pull" ? "readings-first" : ""}`}>
          <div>
            <h2 className="brew-heading" id="brew-pull">
              Pull
              {!brewed && stage !== "pull" && showPulls ? (
                <button type="button" className="link-button" onClick={() => setShowPulls(false)}>
                  Fold
                </button>
              ) : null}
            </h2>
            {brewed ? (
              <p className="brew-note">
                <Check size={16} /> Taken from inventory
                {b.brewedAt ? ` ${showDateTime(b.brewedAt)}` : ""}. Undo puts it all back.
              </p>
            ) : null}
            {toAssign.length ? (
              <p className="brew-note brew-note-flag">
                <TriangleAlert size={16} /> Some of what this brew used was not in inventory. Once
                it is received or counted, assign it to its lot below. The brew is flagged until
                then.
              </p>
            ) : null}
            {(folded ? pullLines.filter((l) => attention.has(l.recipeLineId)) : pullLines).map(
              (l) => (
                <PullCard
                  key={l.recipeLineId}
                  line={l}
                  pulls={draft.pulls[l.recipeLineId] ?? []}
                  readOnly={brewed}
                  standing={standing}
                  onChange={(next) => setPulls(l.recipeLineId, next)}
                  onReplan={() => l.item && replan(l.item.id)}
                  onStock={(mode) => l.item && setStocking({ itemId: l.item.id, mode })}
                  onAssign={(input, key) => send.assignBrewPull({ id: b.id, ...input }, key)}
                />
              ),
            )}
            {folded && ready.length ? (
              <section className="brew-card pulled">
                <div className="pulled-head">
                  <strong>
                    Pulled {ready.length} of {pullLines.length}
                  </strong>
                  <button type="button" className="button" onClick={() => setShowPulls(true)}>
                    Show the pulls
                  </button>
                </div>
                <ul className="pulled-list">
                  {ready.map((l) => (
                    <li key={l.recipeLineId}>
                      <span>{lineName(l)}</span>
                      <span className="num">
                        {pulledLine(l, draft.pulls[l.recipeLineId] ?? [])}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {unlinked.length ? (
              <section className="brew-card">
                <strong>Not taken from inventory</strong>
                <p className="muted">
                  {unlinked.map((l) => l.name).join(", ")}.{" "}
                  {unlinked.length === 1 ? "It is" : "They are"} not linked to inventory on the
                  recipe, so {unlinked.length === 1 ? "it is" : "they are"} not pulled here.
                </p>
              </section>
            ) : null}
            {ibuBecause ? (
              <p className="brew-note">
                With {ibuBecause} it comes to <strong>{e.ibuAsBrewed.toFixed(1)} IBU</strong>, where
                the recipe says {e.ibu.toFixed(1)}.
              </p>
            ) : null}
          </div>
          <div>
            {/* In the day's order, mash first (Scott, 2026-10-02). */}
            <h2 className="brew-heading">Readings</h2>
            <section className="brew-card brew-readings">
              <StageHeading id="mash" title="Mash" stage={stage} brewed={brewed} />
              <ReadingField
                label="Strike temp"
                unit="°F"
                value={draft.strikeTempF}
                onChange={(v) => set({ strikeTempF: v })}
              />
              <ReadingField
                label="Mash temp"
                unit="°F"
                value={draft.mashTempF}
                hint={
                  e.mashTempC === null ? undefined : `Recipe says ${showFahrenheit(e.mashTempC)}`
                }
                onChange={(v) => set({ mashTempF: v })}
              />
              <ReadingField
                label="Mash pH"
                value={draft.mashPh}
                onChange={(v) => set({ mashPh: v })}
              />
              <StageHeading id="boil" title="Boil" stage={stage} brewed={brewed} />
              <ReadingField
                label="Pre-boil volume"
                unit="gal"
                value={draft.preBoilGal}
                estimate={gallons(e.preBoilLiters, 1)}
                onChange={(v) => set({ preBoilGal: v })}
              />
              <ReadingField
                label="Pre-boil gravity"
                value={draft.preBoilGravity}
                estimate={showGravity(e.preBoilGravity)}
                onChange={(v) => set({ preBoilGravity: v })}
              />
              {/* How hot the wort was as each whirlpool step went in, which the day decides (Scott's
                  Jam, 2026-10-08). A step after the first starts from the first's, to change only
                  where it differs (Tyler, 2026-10-19). */}
              {steps.map((step, i) => {
                const key = stepKey(step);
                const typed = draft.whirlpoolF[key] ?? "";
                const fromFirst = i > 0 && !typed ? firstStepF : "";
                const recipeTemp = step[0]?.tempC ?? null;
                const recipeF = recipeTemp === null ? null : showFahrenheit(recipeTemp);
                return (
                  <ReadingField
                    key={key}
                    label={
                      steps.length === 1 ? "Whirlpool temp" : `Whirlpool temp: ${hopNames(step)}`
                    }
                    unit="°F"
                    value={typed}
                    placeholder={fromFirst || undefined}
                    hint={
                      fromFirst
                        ? `As the first step${recipeF ? `; recipe says ${recipeF}` : ""}`
                        : recipeF
                          ? `Recipe says ${recipeF}`
                          : undefined
                    }
                    onChange={(v) =>
                      setDraft((d) => ({ ...d, whirlpoolF: { ...d.whirlpoolF, [key]: v } }))
                    }
                  />
                );
              })}
              <StageHeading id="fermenter" title="Fermenter" stage={stage} brewed={brewed} />
              <ReadingField
                label="Original gravity"
                value={draft.og}
                estimate={showGravity(e.og)}
                onChange={(v) => set({ og: v })}
              />
              <ReadingField
                label="Into the fermenter"
                unit="gal"
                value={draft.fermenterGal}
                estimate={gallons(b.batchLiters, 1)}
                onChange={(v) => set({ fermenterGal: v })}
              />
            </section>
            {b.readings.fg === null ? null : (
              <p className="brew-note">
                Final gravity <strong>{showGravity(Number(b.readings.fg))}</strong>, recorded
                earlier.
              </p>
            )}

            {yeastLines.length ? (
              <>
                <h2 className="brew-heading" id="brew-yeast">
                  Yeast
                </h2>
                {yeastLines.map((l) => (
                  <PullCard
                    key={l.recipeLineId}
                    line={l}
                    brew={b}
                    pulls={draft.pulls[l.recipeLineId] ?? []}
                    readOnly={brewed}
                    standing={standing}
                    onChange={(next) => setPulls(l.recipeLineId, next)}
                    onReplan={() => l.item && replan(l.item.id)}
                    onStock={(mode) => l.item && setStocking({ itemId: l.item.id, mode })}
                    onAssign={(input, key) => send.assignBrewPull({ id: b.id, ...input }, key)}
                  />
                ))}
              </>
            ) : null}
            {brewed && harvestable(b).length ? (
              <>
                <h2 className="brew-heading" id="brew-harvest">
                  Harvest
                </h2>
                <section className="brew-card">
                  {b.harvests.length ? (
                    <ul className="pulled-list harvest-list">
                      {b.harvests.map((h) => (
                        <li key={h.movementId}>
                          <span>
                            G{h.generation} harvest, {showDate(h.harvestedOn)}
                          </span>
                          <span className="num">
                            {inUnit(h.quantity, "lb")} lb · {h.locationName}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted">
                      Harvest its yeast to pitch it again: it goes into inventory a generation on,
                      and brew day offers it first.
                    </p>
                  )}
                  {b.yeastCost.map((c) => (
                    <p key={c.itemId} className="muted yeast-cost">
                      {yeastCostText(c, b.yeastCost.length > 1)}
                    </p>
                  ))}
                  <div className="pull-fixes">
                    <button type="button" className="button" onClick={() => setHarvesting(true)}>
                      <FlaskConical size={16} /> Harvest yeast
                    </button>
                  </div>
                </section>
              </>
            ) : null}

            <h2 className="brew-heading">Notes</h2>
            <section className="brew-card">
              <textarea
                className="input"
                rows={4}
                aria-label="Notes"
                value={draft.notes}
                placeholder="Anything worth remembering about today…"
                onChange={(ev) => set({ notes: ev.target.value })}
              />
            </section>

            {/* Just above Brewed, which puts the beer in it. */}
            <h2 className="brew-heading" id="brew-tanks">
              Tanks
            </h2>
            <BrewTanks brew={b} />
          </div>
        </div>
        {/* Last on the page, below both columns, and away from the recipe's button it was easy to
            mistake it for. */}
        {brewed ? null : (
          <div className="brew-delete">
            <button type="button" className="button danger" onClick={() => setConfirming("delete")}>
              <Trash2 size={18} /> Delete this brew
            </button>
          </div>
        )}
        <ErrorNote error={undo.error} />
      </div>

      <div className="brew-actions">
        {brewed ? (
          <span className="brew-save">
            {b.brewedAt ? `Brewed ${showDateTime(b.brewedAt)}` : "Brewed"}
          </span>
        ) : (
          <SaveState status={autosave.status} />
        )}
        {brewed ? (
          <button type="button" className="button" onClick={() => setConfirming("undo")}>
            <RotateCcw size={18} /> Undo
          </button>
        ) : (
          <button
            type="button"
            className="button primary"
            disabled={brewIt.isPending}
            onClick={() => setConfirming("brew")}
          >
            <Check size={18} /> Brewed
          </button>
        )}
      </div>

      <Confirm
        open={confirming === "brew"}
        onClose={() => setConfirming(null)}
        title="Take it from inventory?"
        action="Brewed"
        busy={brewIt.isPending}
        error={brewIt.error}
        onConfirm={() => brewIt.mutate(undefined)}
      >
        <p>
          {pulls.length === 0
            ? "Nothing is set to be pulled, so nothing leaves inventory."
            : `${pulls.length} ${pulls.length === 1 ? "pull comes" : "pulls come"} out of inventory now, as one entry that can be undone:`}
        </p>
        {pulls.length ? (
          <ul className="confirm-pulls">
            {linked
              .filter((l) =>
                (draft.pulls[l.recipeLineId] ?? []).some((p) => amountOf(p.amount) > 0),
              )
              .map((l) => (
                <li key={l.recipeLineId}>
                  <span>{lineName(l)}</span>
                  <span className="num">{pulledLine(l, draft.pulls[l.recipeLineId] ?? [])}</span>
                </li>
              ))}
          </ul>
        ) : null}
        {into.length ? (
          <p>
            The beer goes into <strong>{into.map((s) => s.tank.name).join(" and ")}</strong>.
          </p>
        ) : (
          <p className="form-warning">
            No tank is booked for it, so the cellar won't show where it is. Pick one under{" "}
            <strong>Tanks</strong> first, or add it later from its page.
          </p>
        )}
        {notInInventory.length ? (
          <p className="form-warning">
            Not in inventory: {notInInventory.map(lineName).join(", ")}. What its lots do not hold
            is recorded on the brew, which is flagged until you assign it to the lot it came from.
          </p>
        ) : null}
        {shortLines.length ? (
          <p className="form-error">
            Less than the recipe asks for: {shortLines.map(lineName).join(", ")}.
          </p>
        ) : null}
      </Confirm>
      <Confirm
        open={confirming === "undo"}
        onClose={() => setConfirming(null)}
        title="Undo this brew?"
        action="Undo"
        busy={undo.isPending}
        error={undo.error}
        onConfirm={() => undo.mutate(undefined)}
      >
        <p>
          Everything it took goes back into inventory, including anything assigned to a lot since,
          and it is in progress again.
          {b.harvests.length
            ? " Its yeast was harvested, so undo the harvest first, from its lot's history."
            : ""}
        </p>
      </Confirm>
      <Confirm
        open={confirming === "delete"}
        onClose={() => setConfirming(null)}
        title="Delete this brew?"
        action="Delete"
        danger
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => remove.mutate(undefined)}
      >
        <p>Nothing was taken from inventory for it, so nothing changes there.</p>
      </Confirm>
      <PrintSheets recipe={recipe} brew={b} />
      <HarvestDialog open={harvesting} onClose={() => setHarvesting(false)} brew={b} />
      {/* A delivery not entered yet, or a count, made here so nothing typed on this page is lost. */}
      <MovementDialog
        open={stocking !== null}
        onClose={() => setStocking(null)}
        mode={stocking?.mode ?? "receive"}
        itemId={stocking?.itemId}
      />
    </>
  );
}
/**
 * What a brewed brew's yeast cost, by the halving method (Tyler, 2026-10-07): it keeps half of what it
 * pitched, and its harvest carries the other half on to the next brew; with no harvest it keeps it all.
 */
function yeastCostText(c: BrewDetail["yeastCost"][number], named: boolean): string {
  const of = named ? `${c.name}: ` : "";
  if (c.pitched === null) {
    return `${of}What its yeast cost is not known: it was harvested before the app, with no price.`;
  }
  const some = c.partial ? " Some of it has no known cost, so this is what is known." : "";
  return dec(c.carried).gt(ZERO)
    ? `${of}Its yeast cost ${formatMoney(c.pitched)}. Its harvest carries half on, ${formatMoney(
        c.carried,
      )}, so this brew keeps ${formatMoney(c.kept ?? "0")}.${some}`
    : `${of}Its yeast cost ${formatMoney(c.pitched)}, all of it this brew's until it is harvested: a harvest carries half on to the next brew.${some}`;
}
/** A stage's heading among the readings: done, the one it is at now, or still to come. */
function StageHeading({
  id,
  title,
  stage,
  brewed,
}: {
  id: Stage;
  title: string;
  stage: Stage;
  brewed: boolean;
}) {
  const now = stage === id && !brewed;
  const done = STAGES.findIndex((x) => x.id === id) < STAGES.findIndex((x) => x.id === stage);
  return (
    <h3 className={`brew-stage ${now ? "now" : ""}`} id={`brew-${id}`}>
      {done && !brewed ? <Check size={16} aria-label="Done" /> : null}
      {title}
      {now ? <span className="brew-now">Now</span> : null}
    </h3>
  );
}
/** Where saving is, at the foot of a page that saves itself, in the page's own words while idle. */
export function SaveState({ status, idle }: { status: SaveStatus; idle?: string }) {
  return (
    <span className={`brew-save ${status}`} role="status">
      {status === "retrying" ? <TriangleAlert size={14} /> : <Save size={14} />}{" "}
      {saveStatusText(status, idle)}
    </span>
  );
}
function Confirm({
  open,
  onClose,
  title,
  action,
  busy,
  error,
  onConfirm,
  children,
  danger = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  action: string;
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  children: ReactNode;
  /** A step that cannot be undone: confirmed in red, not as the page's main action. */
  danger?: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <DialogForm
        onSubmit={onConfirm}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className={`button ${danger ? "danger-fill" : "primary"}`}
              disabled={busy}
            >
              {action}
            </button>
          </>
        }
      >
        {children}
        <ErrorNote error={error} />
      </DialogForm>
    </Dialog>
  );
}
/** A whirlpool step's hops by name, each once: "Amarillo, Centennial, El Dorado". */
const hopNames = (step: BrewLine[]) =>
  [...new Set(step.map((l) => l.item?.name ?? l.name))].join(", ");
/**
 * What makes the bitterness on the day differ from the recipe's, for the line under the pulls: the
 * lots' own alpha, the whirlpool temperatures typed, or both; null when it comes to the recipe's.
 */
function ibuReasons(b: BrewDetail, steps: BrewLine[][]): string | null {
  const e = b.estimates;
  const shown = (ibu: number) => ibu.toFixed(1);
  const reasons: string[] = [];
  if (shown(e.ibuWithLots) !== shown(e.ibu)) reasons.push("these lots' own alpha");
  if (shown(e.ibuAsBrewed) !== shown(e.ibuWithLots)) {
    const typed = b.readings.whirlpoolTemps;
    const temps = [...new Set(typed.map((w) => showFahrenheit(w.tempC)))];
    // One temperature for the whole whirlpool: the first step's, which the rest take unless typed.
    const firstTyped = typed.some((w) => steps[0]?.some((l) => l.recipeLineId === w.recipeLineId));
    reasons.push(
      temps.length === 1 && (firstTyped || steps.length === 1)
        ? `the whirlpool at ${temps[0]}`
        : "the whirlpool temperatures typed",
    );
  }
  return reasons.length ? reasons.join(" and ") : null;
}
function ReadingField({
  label,
  unit,
  value,
  estimate,
  hint,
  placeholder,
  onChange,
}: {
  label: string;
  unit?: string;
  value: string;
  estimate?: string;
  hint?: string;
  /** What it is taken as while empty. */
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const input = (
    <input
      id={id}
      className="input num"
      inputMode="decimal"
      enterKeyHint="next"
      autoComplete="off"
      placeholder={placeholder}
      value={value}
      onChange={(ev) => onChange(decimalOnly(ev.target.value))}
    />
  );
  return (
    <div className="brew-field">
      <label htmlFor={id}>{label}</label>
      {unit ? (
        <span className="input-unit">
          {input}
          <span className="unit">{unit}</span>
        </span>
      ) : (
        input
      )}
      <span className="faint">
        {estimate ? `Estimated ${estimate}${unit ? ` ${unit}` : ""}` : (hint ?? "")}
      </span>
    </div>
  );
}
